

import { useState, useEffect } from "react";
import { X, Trash2, Plus } from "lucide-react";
import { toast } from "../../../lib/toast";
import { Button } from "../../../components/ui/button";
import { CharLimitIndicator, useCharLimitHandlers } from "../../../components/CharLimitIndicator";
import type { ItemAta } from "../../../services/itemAta.service";
import type { Desenvolvimento } from "../../../services/desenvolvimento.service";
import { LIMITS, getCharCountColor, getCharCountMessage, getCharCountStatus } from "../../../lib/constants";

interface ItemEditorProps {
  item: ItemAta;
  desenvolvimentos: Desenvolvimento[];
  topicos: Array<{ id: string; titulo: string }>;
  participantes: Array<{ id: string; nome: string; email: string }>;
  meetingNumber: number;
  lastMeetingNumber: number;
  onSave: (
    itemId: string,
    data: {
      descricao: string;
      entidade?: string;
      prazo?: string;
      topico_id?: string;
    }
  ) => Promise<void>;
  onDelete: (itemId: string) => Promise<void>;
  onClose: () => void;
  onAddDesenvolvimento: (itemId: string, texto: string) => Promise<void>;
  onUpdateDesenvolvimento: (
    desenvolvimentoId: string,
    texto: string
  ) => Promise<void>;
  onDeleteDesenvolvimento: (desenvolvimentoId: string) => Promise<void>;
}

export function ItemEditor({
  item,
  desenvolvimentos,
  topicos,
  participantes,
  meetingNumber,
  lastMeetingNumber,
  onSave,
  onDelete,
  onClose,
  onAddDesenvolvimento,
  onUpdateDesenvolvimento,
  onDeleteDesenvolvimento,
}: ItemEditorProps) {
  const [descricao, setDescricao] = useState(item.descricao);
  const [entidade, setEntidade] = useState(item.entidade || "");
  const [prazo, setPrazo] = useState(item.prazo || "");
  const [topicoId] = useState(item.topico_id || "");
  const [newDevText, setNewDevText] = useState("");
  const [editingUpdateId, setEditingUpdateId] = useState<string | null>(null);
  const [editingUpdateText, setEditingUpdateText] = useState("");

  const descHandlers = useCharLimitHandlers(LIMITS.ITEM_DESCRIPTION_MAX, setDescricao);
  const newDevHandlers = useCharLimitHandlers(LIMITS.DEVELOPMENT_TEXT_MAX, setNewDevText);
  const editDevHandlers = useCharLimitHandlers(LIMITS.DEVELOPMENT_TEXT_MAX, setEditingUpdateText);
  const [saving, setSaving] = useState(false);

  const itemDevs = desenvolvimentos
    .filter((d) => d.item_id === item.id)
    .sort((a, b) => a.ata_numero - b.ata_numero);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleSave = async () => {
    if (!descricao.trim()) {
      toast.error("Descrição é obrigatória. Descreva o que precisa ser feito neste item.");
      return;
    }
    
    if (descricao.trim().length > LIMITS.ITEM_DESCRIPTION_MAX) {
      toast.error(`Descrição excedeu o limite de ${LIMITS.ITEM_DESCRIPTION_MAX} caracteres. Reduza o texto para poder salvar.`);
      return;
    }

    if (entidade && entidade.length > LIMITS.ITEM_ENTITY_MAX) {
      toast.error(`Entidade/Responsável excedeu o limite de ${LIMITS.ITEM_ENTITY_MAX} caracteres.`);
      return;
    }

    if (prazo && isNaN(Date.parse(prazo))) {
      toast.error("Data de prazo inválida. Use o seletor de data ou o formato AAAA-MM-DD.");
      return;
    }
    
    setSaving(true);
    try {
      await onSave(item.id, {
        descricao: descricao.trim(),
        entidade: entidade.trim() || undefined,
        prazo: prazo || undefined,
        topico_id: topicoId || undefined,
      });
      onClose();
    } catch (error) {
      console.error('Erro ao salvar item:', error);
      const errMsg = error instanceof Error ? error.message : '';
      if (errMsg.includes('conexão') || errMsg.includes('internet')) {
        toast.error('Não foi possível salvar o item — verifique sua conexão com a internet.');
      } else {
        toast.error(errMsg || 'Não foi possível salvar o item. Verifique os dados e tente novamente.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAddDev = async () => {
    if (!newDevText.trim()) {
      toast.error("Texto do desenvolvimento é obrigatório. Descreva o que foi feito.");
      return;
    }
    if (newDevText.trim().length > LIMITS.DEVELOPMENT_TEXT_MAX) {
      toast.error(`Texto excedeu o limite de ${LIMITS.DEVELOPMENT_TEXT_MAX} caracteres. Reduza o texto.`);
      return;
    }
    // Verificar limite de desenvolvimentos
    if (itemDevs.length >= LIMITS.MAX_DEVELOPMENTS_PER_ITEM) {
      toast.error(`Limite de ${LIMITS.MAX_DEVELOPMENTS_PER_ITEM} desenvolvimentos por item atingido.`);
      return;
    }
    await onAddDesenvolvimento(item.id, newDevText.trim());
    setNewDevText("");
  };

  const handleUpdateDev = async (devId: string) => {
    if (!editingUpdateText.trim()) return;
    await onUpdateDesenvolvimento(devId, editingUpdateText.trim());
    setEditingUpdateId(null);
    setEditingUpdateText("");
  };

  const handleDeleteDev = async (devId: string) => {
    await onDeleteDesenvolvimento(devId);
  };

  return (
    <>
      {}
      <div
        className="fixed inset-0 z-60 fade-in"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        onClick={onClose}
      />

      {}
      <aside
        className="w-full md:w-[40rem] border-l border-border flex flex-col bg-background fixed right-0 top-0 bottom-0 z-70"
        style={{
          height: "100vh",
          animation: "slideInRight 300ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {}
        <div className="p-4 border-b border-border flex items-center justify-between flex-shrink-0">
          <h3 className="font-semibold text-foreground">Editar Item</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded transition-colors"
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        {}
        <div
          className="flex-1 overflow-y-auto p-6 space-y-6"
          style={{ maxHeight: "calc(100vh - 120px)" }}
        >
          {}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">Descrição</label>
              <CharLimitIndicator current={descricao.length} max={LIMITS.ITEM_DESCRIPTION_MAX} />
            </div>
            <textarea
              value={descricao}
              onChange={descHandlers.onChange}
              onPaste={descHandlers.onPaste}
              maxLength={LIMITS.ITEM_DESCRIPTION_MAX}
              rows={4}
              className="w-full px-3 py-2 border border-border rounded bg-background text-foreground"
            />
          </div>

          {}
          <div>
            <label className="block mb-2 text-sm font-medium">Responsável</label>
            <select
              value={entidade}
              onChange={(e) => setEntidade(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded bg-background text-foreground"
            >
              <option value="">Selecione um responsável</option>
              {participantes.map((p) => (
                <option key={p.id} value={p.nome}>
                  {p.nome}
                </option>
              ))}
            </select>
          </div>

          {}
          <div>
            <label className="block mb-2 text-sm font-medium">Prazo</label>
            <input
              type="date"
              value={prazo}
              onChange={(e) => setPrazo(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded bg-background text-foreground"
            />
          </div>

          {}
          <div>
            <label className="block mb-2 text-sm font-medium">
              Desenvolvimentos
            </label>
            <div className="space-y-2 mb-3">
              {itemDevs.map((dev) => {
                const ataNumero = dev.ata_numero ?? 0;
                const isNew = ataNumero === meetingNumber;
                return (
                  <div
                    key={dev.id}
                    className="p-3 border border-border rounded flex items-start gap-2 bg-muted/30"
                  >
                    <div className="flex-1">
                      <div className="text-xs mb-1 font-semibold flex items-center gap-2">
                        {isNew && (
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-bold">
                            NOVO
                          </span>
                        )}
                        <span className={isNew ? "text-primary" : "text-muted-foreground"}>
                          A{ataNumero || '-'} |
                        </span>
                      </div>
                      {editingUpdateId === dev.id ? (
                        <div className="space-y-1">
                          <textarea
                            value={editingUpdateText}
                            onChange={editDevHandlers.onChange}
                            onPaste={editDevHandlers.onPaste}
                            maxLength={LIMITS.DEVELOPMENT_TEXT_MAX}
                            onBlur={() => {
                              if (editingUpdateText.trim()) {
                                handleUpdateDev(dev.id);
                              } else {
                                setEditingUpdateId(null);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                if (e.ctrlKey) {
                                  // Ctrl+Enter: inserir nova linha na posição do cursor
                                  e.preventDefault();
                                  const target = e.target as HTMLTextAreaElement;
                                  const start = target.selectionStart;
                                  const end = target.selectionEnd;
                                  const newValue = editingUpdateText.substring(0, start) + "\n" + editingUpdateText.substring(end);
                                  setEditingUpdateText(newValue);
                                  // Mover cursor para depois da nova linha
                                  setTimeout(() => {
                                    target.selectionStart = target.selectionEnd = start + 1;
                                  }, 0);
                                  return;
                                }
                                // Enter simples: salva
                                if (editingUpdateText.trim()) {
                                  e.preventDefault();
                                  handleUpdateDev(dev.id);
                                }
                              }
                              if (e.key === "Escape") {
                                setEditingUpdateId(null);
                                setEditingUpdateText("");
                              }
                            }}
                            autoFocus
                            rows={12}
                            className="w-full px-2 py-1 border rounded resize-y min-h-[280px] bg-background text-foreground"
                            style={{ borderColor: "#013EFB" }}
                          />
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] text-muted-foreground">
                              <kbd className="px-1 py-0.5 bg-muted rounded font-semibold border border-border">Enter</kbd> para salvar • <kbd className="px-1 py-0.5 bg-muted rounded font-semibold border border-border">Esc</kbd> para cancelar
                            </span>
                            <CharLimitIndicator current={editingUpdateText.length} max={LIMITS.DEVELOPMENT_TEXT_MAX} />
                          </div>
                        </div>
                      ) : (
                        <div
                          onClick={() => {
                            setEditingUpdateId(dev.id);
                            setEditingUpdateText(dev.texto);
                          }}
                          className={`cursor-pointer hover:bg-muted px-2 py-1 rounded whitespace-pre-line ${isNew ? "font-bold text-primary" : "font-normal text-muted-foreground"}`}
                        >
                          {dev.texto}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteDev(dev.id)}
                      className="p-1 hover:bg-red-100 rounded transition-colors"
                      title="Excluir desenvolvimento"
                      aria-label="Excluir desenvolvimento"
                    >
                      <Trash2 size={16} className="text-red-600" />
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Input para novo desenvolvimento com botão */}
            <div className="flex flex-col gap-2">
              <textarea
                value={newDevText}
                onChange={newDevHandlers.onChange}
                onPaste={newDevHandlers.onPaste}
                maxLength={LIMITS.DEVELOPMENT_TEXT_MAX}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    if (e.ctrlKey) {
                      // Ctrl+Enter: inserir nova linha na posição do cursor
                      e.preventDefault();
                      const target = e.target as HTMLTextAreaElement;
                      const start = target.selectionStart;
                      const end = target.selectionEnd;
                      const newValue = newDevText.substring(0, start) + "\n" + newDevText.substring(end);
                      setNewDevText(newValue);
                      // Mover cursor para depois da nova linha
                      setTimeout(() => {
                        target.selectionStart = target.selectionEnd = start + 1;
                      }, 0);
                      return;
                    }
                    // Enter simples: salva
                    if (newDevText.trim()) {
                      e.preventDefault();
                      handleAddDev();
                    }
                  }
                }}
                placeholder="Digite o desenvolvimento aqui...&#10;&#10;Use Ctrl+Enter para criar nova linha."
                rows={8}
                className="w-full px-3 py-2 border border-border rounded resize-y min-h-[180px] bg-background text-foreground"
                disabled={saving}
              />
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">
                  💡 <kbd className="px-1.5 py-0.5 bg-muted rounded text-foreground font-semibold text-[10px] border border-border">Enter</kbd> adicionar • <kbd className="px-1.5 py-0.5 bg-muted rounded text-foreground font-semibold text-[10px] border border-border">Ctrl+Enter</kbd> nova linha
                </span>
                <CharLimitIndicator current={newDevText.length} max={LIMITS.DEVELOPMENT_TEXT_MAX} />
                <button
                  onClick={handleAddDev}
                  disabled={!newDevText.trim() || saving}
                  className="px-4 py-2 rounded font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
                  title="Adicionar desenvolvimento (Ctrl+Enter)"
                >
                  <Plus size={18} />
                  Adicionar
                </button>
              </div>
            </div>
          </div>
        </div>

        {}
        <div
          className="sticky bottom-0 p-4 border-t border-border flex gap-2 bg-background flex-shrink-0 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]"
        >
          <Button
            onClick={onClose}
            variant="outline"
            className="flex-1"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            disabled={!descricao.trim() || saving}
            variant="default"
            className="flex-1"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Salvando...
              </>
            ) : (
              'Salvar'
            )}
          </Button>
        </div>
      </aside>
    </>
  );
}
